import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@/lib/server-fn-shim";
import { toast } from "sonner";
import { pageMeta } from "@/lib/seo";
import { getMyTrip } from "@/lib/trip.functions";
import {
  adminListFamilyShareLinks,
  adminRevokeFamilyShareLink,
  adminSetFamilyFollowDisabled,
  getFamilyFollowTripState,
} from "@/lib/family-follow.functions";
import { PageHeader } from "@/components/admin/PageHeader";
import { RoavaCard, StatusChip, DangerButton, SecondaryButton } from "@/components/roava";
import { Switch } from "@/components/ui/switch";

export const Route = createFileRoute("/_authenticated/admin/family")({
  head: () =>
    pageMeta({
      title: "Family Follow — Admin · Roava",
      description: "Manage family-follow share links and the trip-wide kill switch.",
      path: "/admin/family",
    }),
  component: AdminFamily,
});

function AdminFamily() {
  const qc = useQueryClient();
  const tripFn = useServerFn(getMyTrip);
  const listFn = useServerFn(adminListFamilyShareLinks);
  const stateFn = useServerFn(getFamilyFollowTripState);
  const revokeFn = useServerFn(adminRevokeFamilyShareLink);
  const setDisabledFn = useServerFn(adminSetFamilyFollowDisabled);

  const { data: tripData } = useQuery({ queryKey: ["my-trip"], queryFn: () => tripFn({}) });
  const tripId = tripData?.trip?.id;

  const { data, isLoading } = useQuery({
    queryKey: ["admin-family-links", tripId],
    queryFn: () => listFn({ data: { tripId: tripId! } }),
    enabled: !!tripId,
  });
  const { data: killState } = useQuery({
    queryKey: ["admin-family-kill", tripId],
    queryFn: () => stateFn({ data: { tripId: tripId! } }),
    enabled: !!tripId,
  });

  const setKill = useMutation({
    mutationFn: (disabled: boolean) =>
      setDisabledFn({ data: { tripId: tripId!, disabled } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-family-kill", tripId] }),
    onError: (e: any) => toast.error(e?.message ?? "Failed"),
  });

  const revoke = useMutation({
    mutationFn: (id: string) => revokeFn({ data: { id } }),
    onSuccess: () => {
      toast.success("Link revoked");
      qc.invalidateQueries({ queryKey: ["admin-family-links", tripId] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed to revoke"),
  });

  return (
    <main className="mx-auto max-w-3xl px-5 md:px-8 py-6 md:py-8 grid gap-6 overflow-x-hidden">
      <PageHeader
        title="Family Follow"
        subtitle="Manage family-share links and the trip-wide kill switch."
      />

      <RoavaCard
        title="Trip-wide kill switch"
        description="Instantly blocks every family viewer for this trip without revoking individual links."
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium">
              Family Follow is currently {killState?.disabled ? "DISABLED" : "enabled"}
            </p>
            <p className="text-xs text-[var(--roava-text-muted)]">
              Travelers can still adjust their own settings — this flag overrides them at the server.
            </p>
          </div>
          <Switch
            checked={!killState?.disabled}
            onCheckedChange={(v) => setKill.mutate(!v)}
            disabled={!tripId || setKill.isPending}
          />
        </div>
      </RoavaCard>

      <section>
        <h2 className="text-lg font-semibold mb-3">All family-share links</h2>
        {isLoading && <p className="text-sm text-[var(--roava-text-muted)]">Loading…</p>}
        {!isLoading && (data?.links ?? []).length === 0 && (
          <p className="text-sm text-[var(--roava-text-muted)]">No family-share links yet.</p>
        )}
        <div className="grid gap-2">
          {(data?.links ?? []).map((l) => {
            const status = l.revoked_at
              ? { v: "danger" as const, t: "revoked" }
              : !l.enabled
              ? { v: "neutral" as const, t: "traveler off" }
              : l.paused
              ? { v: "warning" as const, t: "paused" }
              : new Date(l.expires_at).getTime() < Date.now()
              ? { v: "neutral" as const, t: "expired" }
              : { v: "success" as const, t: "active" };
            return (
              <RoavaCard key={l.id} flush className="p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">
                      {l.display_name ?? "Traveler"}
                      {l.label ? ` · ${l.label}` : ""}
                    </div>
                    <div className="text-[11px] text-[var(--roava-text-muted)]">
                      {l.view_count} views · created {new Date(l.created_at).toLocaleDateString()} · expires{" "}
                      {new Date(l.expires_at).toLocaleDateString()}
                      {l.last_viewed_at
                        ? ` · last viewed ${new Date(l.last_viewed_at).toLocaleString()}`
                        : ""}
                    </div>
                  </div>
                  <StatusChip variant={status.v}>{status.t}</StatusChip>
                  {!l.revoked_at && (
                    <DangerButton size="sm" onClick={() => revoke.mutate(l.id)}>
                      Revoke
                    </DangerButton>
                  )}
                </div>
              </RoavaCard>
            );
          })}
        </div>
        <p className="mt-3 text-xs text-[var(--roava-text-muted)]">
          Note: revoking a link is permanent. To temporarily silence family viewers, use the kill switch above
          or ask the traveler to pause from their own Family Follow card.
        </p>
      </section>
    </main>
  );
}
